import { isExpectedResponse } from './ResponseDiagnostics';

export interface FetchRequestConfig {
    url: string;
    method: string;
    headers?: Record<string, string>;
    body?: unknown;
}

export interface FetchResponse {
    status: number;
    body: unknown;
}

function requestBody(body: unknown, headers: Headers): BodyInit | undefined {
    if (body === undefined || body === null) {
        return undefined;
    }
    if (typeof body === 'string') {
        if (!headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/x-www-form-urlencoded');
        }
        return body;
    }
    if (
        body instanceof FormData ||
        body instanceof Blob ||
        body instanceof URLSearchParams ||
        body instanceof ArrayBuffer
    ) {
        return body;
    }
    if (ArrayBuffer.isView(body)) {
        return body as BodyInit;
    }
    if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    return JSON.stringify(body);
}

function responseBody(text: string, contentType: string): unknown {
    if (!text) {
        return null;
    }
    if (/[/+]json(?:;|$)/i.test(contentType)) {
        return JSON.parse(text) as unknown;
    }
    if (contentType.split(';')[0] === 'application/x-www-form-urlencoded') {
        return Object.fromEntries(new URLSearchParams(text));
    }
    return null;
}

export async function fetchRequest(
    config: FetchRequestConfig,
    signal: AbortSignal | undefined,
    defaultHeaders: Record<string, string>,
): Promise<FetchResponse | null> {
    try {
        const headers = new Headers(defaultHeaders);
        for (const [name, value] of Object.entries(config.headers ?? {})) {
            headers.set(name, value);
        }
        const body =
            config.method === 'GET' || config.method === 'HEAD'
                ? undefined
                : requestBody(config.body, headers);
        const response = await fetch(config.url, {
            method: config.method,
            headers,
            body,
            signal,
            credentials: 'same-origin',
        });
        const text = await response.text();
        if (
            !isExpectedResponse(
                { url: config.url, method: config.method },
                {
                    status: response.status,
                    type: (response.headers.get('Content-Type') ?? '')
                        .split(';')[0]
                        .trim()
                        .toLowerCase(),
                    text,
                    headers: Object.fromEntries(response.headers),
                    url: response.url,
                },
            )
        ) {
            return { status: 408, body: null };
        }
        return {
            status: response.status,
            body: responseBody(
                text,
                response.headers.get('Content-Type') ?? '',
            ),
        };
    } catch {
        if (signal?.aborted) {
            return null;
        }
        // Legacy offline navigation and retry handling depend on status 408.
        return { status: 408, body: null };
    }
}
